import { DatePicker, DatePickerProps } from '@mui/x-date-pickers';
import { alpha } from '@mui/material/styles';
import { StyleObj } from '../../@types';
import useLocalization from '../../hooks/useLocalization';

const styles: StyleObj = {
  datePicker: (theme) => ({
    width: '100%',
    minWidth: 0,
    px: 0,
    '& .MuiInputLabel-root': {
      transform: {
        xs: 'translate(14px, 10px) scale(1)',
        md: 'translate(14px, 16px) scale(1)',
      },
      '&.MuiInputLabel-shrink': {
        transform: {
          xs: 'translate(14px, -9px) scale(0.75)',
          md: 'translate(14px, -9px) scale(0.75)',
        },
      },
    },
    '& .MuiInputBase-input.MuiOutlinedInput-input, & .MuiPickersInputBase-sectionsContainer': {
      WebkitTextFillColor: theme.palette.text.primary,
      color: 'text.primary',
      minWidth: 0,
      p: {
        xs: 1.5,
        md: 2,
      },
      fontSize: {
        xs: '0.875rem',
        md: '1rem',
      },
    },
    '& .MuiInputBase-root, & .MuiPickersOutlinedInput-root': {
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
      minHeight: {
        xs: '40px',
        md: '56px',
      },
    },
    '& .MuiPickersOutlinedInput-root': {
      pl: 0,
      pr: {
        xs: 1.5,
        md: 2,
      },
      backgroundColor: alpha(theme.palette.neutral[600], 0.25),
      '& .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: 'neutral.300',
      },
      '&:hover .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: 'neutral.300',
      },
      '&.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
      },
      '&.Mui-error .MuiPickersOutlinedInput-notchedOutline': {
        borderColor: 'error.main',
      },
    },
    '& .MuiButtonBase-root': {
      flexShrink: 0,
      color: 'neutral.300',
      p: {
        xs: 0.75,
        md: 1,
      },
      '& .MuiSvgIcon-root': {
        fontSize: {
          xs: '1.25rem',
          md: '1.5rem',
        },
      },
    },
  }),
  layout: {
    width: 320,
    maxWidth: 'calc(100vw - 32px)',
    backgroundColor: 'background.paper',
    '& .MuiDateCalendar-root': {
      width: '100%',
      maxWidth: 320,
    },
    '.MuiPickersCalendarHeader-label': {
      color: 'text.primary',
      fontWeight: 700,
    },
    '.MuiDayCalendar-weekDayLabel': {
      color: 'text.secondary',
    },
    '.MuiPickersCalendarHeader-switchViewButton, .MuiPickersArrowSwitcher-button': {
      color: 'text.primary',
    },
    '.MuiPickerDay-root': {
      color: 'text.primary',
      '&:hover, &:focus, &.Mui-focusVisible': {
        backgroundColor: 'primary.700',
      },
      '&.MuiPickerDay-today': {
        borderColor: 'text.primary',
      },
      '&.Mui-selected': {
        color: 'primary.contrastText',
        backgroundColor: 'primary.main',
        fontWeight: 700,
        '&:hover, &:focus, &.Mui-focusVisible': {
          backgroundColor: 'primary.main',
        },
      },
    },
    '.MuiYearCalendar-button': {
      color: 'text.primary',
      fontWeight: 700,
      '&:hover, &:focus, &.Mui-focusVisible': {
        backgroundColor: 'primary.700',
      },
      '&.Mui-selected': {
        color: 'primary.contrastText',
        backgroundColor: 'primary.main',
        '&:hover, &:focus, &.Mui-focusVisible': {
          backgroundColor: 'primary.main',
        },
      },
    },
  },
};

interface StyledDatePickerProps extends DatePickerProps {
  error?: boolean;
  helperText?: React.ReactNode;
}

const StyledDatePicker = ({ error, helperText, format = 'DD/MM/YYYY', ...props }: StyledDatePickerProps) => {
  const { t } = useLocalization();
  const selectDatePlaceHolder = t('selectDate');

  return (
    <DatePicker
      sx={styles.datePicker}
      desktopModeMediaQuery='@media (min-width: 0)'
      closeOnSelect
      {...props}
      slotProps={{
        ...props.slotProps,
        field: { ...props.slotProps?.field, readOnly: true },
        layout: {
          ...props.slotProps?.layout,
          sx: styles.layout,
        },
        textField: (ownerState) => {
          const textFieldProps =
            typeof props.slotProps?.textField === 'function'
              ? props.slotProps.textField(ownerState)
              : props.slotProps?.textField;

          return {
            ...textFieldProps,
            error,
            helperText,
            fullWidth: true,
            slotProps: {
              ...textFieldProps?.slotProps,
              htmlInput: {
                ...textFieldProps?.slotProps?.htmlInput,
                placeholder: (props.label as string) || selectDatePlaceHolder,
              },
            },
          };
        },
      }}
      format={format}
    />
  );
};

export default StyledDatePicker;
